/* =============================================================================
   CORE / EXPORTS — iCal-Export
   =============================================================================
   - exportCalendarICS: alle bestätigten/offenen Reservierungen ab heute
     als RFC-5545-konforme .ics-Datei

   S167 — exportFullData / importFullData / openDeleteDataDialog /
   deleteTestReservations / resetAllData entfernt (Jonas): der „Daten"-
   Sub-Sub-Tab + Test-Daten-Generator (seed-test-data.html) sind weg.
============================================================================ */

import {
  todayISO, timeToMinutes, minutesToTime, showToast,
  normalizePreorderItems
} from '../utils/helpers.js';
import { getReservationDuration } from './capacity.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { reservations: [], tables: [], settings: {} };
}

export function exportCalendarICS() {
  const state = getState();
  const tableNamesFn = (typeof window.tableNamesFor === 'function') ? window.tableNamesFor : (ids => (ids || []).join(', '));
  const today = todayISO();
  const upcoming = (state.reservations || [])
    .filter(r => r.date >= today)
    .filter(r => r.status === 'confirmed' || r.status === 'pending')
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  function icsDate(dateStr, timeStr) {
    return dateStr.replace(/-/g, '') + 'T' + timeStr.replace(':', '') + '00';
  }
  function icsEscape(s) {
    return String(s == null ? '' : s)
      .replace(/\\/g, '\\\\')
      .replace(/\n/g, '\\n')
      .replace(/,/g, '\\,')
      .replace(/;/g, '\\;');
  }

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Grill & Green//Reservierungs-Tool//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:' + icsEscape(state.settings.restaurantName || 'Grill & Green') + ' — Reservierungen',
    'X-WR-TIMEZONE:Europe/Berlin'
  ];

  for (const r of upcoming) {
    const startMin = timeToMinutes(r.time);
    const endMin   = startMin + getReservationDuration(r);
    const endTime  = minutesToTime(endMin % (24 * 60));
    // Phase 28 (S26): Tagesfortschreibung über UTC-Math statt
    // `new Date(...) + 86400000` — letzteres ist 23h/25h an DST-Wechseltagen
    // und führt zu falschen Folgedaten 2× im Jahr.
    const endDate  = endMin >= 24 * 60
      ? (() => {
          const [yy, mm, dd] = r.date.split('-').map(Number);
          const next = new Date(Date.UTC(yy, mm - 1, dd) + 86400000);
          const ny = next.getUTCFullYear();
          const nm = String(next.getUTCMonth() + 1).padStart(2, '0');
          const nd = String(next.getUTCDate()).padStart(2, '0');
          return `${ny}-${nm}-${nd}`;
        })()
      : r.date;

    const tableInfo = (r.assignedTables && r.assignedTables.length)
      ? 'Tische: ' + tableNamesFn(r.assignedTables) + (r.tablesCombined ? ' (kombiniert)' : '') + (Number(r.extraChairsUsed) > 0 ? ` + ${Number(r.extraChairsUsed)} Zusatzstuhl${Number(r.extraChairsUsed) > 1 ? 'e' : ''}` : (r.needsExtraChair ? ' + 1 Zusatzstuhl' : ''))
      : 'Tische: noch nicht zugewiesen';

    const desc = [
      r.guests + ' Personen',
      tableInfo,
      r.phone ? 'Telefon: ' + r.phone : '',
      r.email ? 'E-Mail: ' + r.email : '',
      r.notes ? 'Bemerkung: ' + r.notes : '',
      (() => {
        const items = normalizePreorderItems(r.preorder);
        return items.length
          ? 'Vorbestellung: ' + items.map(it => it.qty + 'x ' + it.displayName + (it.note ? ' (' + it.note + ')' : '')).join('; ')
          : '';
      })(),
      'Status: ' + r.status
    ].filter(Boolean).join('\\n');

    const summary = `${r.guests}P · ${r.name}` + (r.status === 'pending' ? ' (offen)' : '');

    lines.push(
      'BEGIN:VEVENT',
      'UID:' + r.id + '@grill-and-green',
      'DTSTAMP:' + new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+/, ''),
      'DTSTART:' + icsDate(r.date, r.time),
      'DTEND:'   + icsDate(endDate, endTime),
      'SUMMARY:' + icsEscape(summary),
      'DESCRIPTION:' + icsEscape(desc),
      'LOCATION:' + icsEscape(state.settings.restaurantName || 'Grill & Green'),
      'STATUS:' + (r.status === 'confirmed' ? 'CONFIRMED' : 'TENTATIVE'),
      'END:VEVENT'
    );
  }
  lines.push('END:VCALENDAR');

  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `grill-and-green-${today}.ics`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  showToast(`${upcoming.length} Reservierungen als Kalender-Datei exportiert`, 'success');
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    exportCalendarICS
  });
}
